import {useEffect, useId, useRef} from 'react';
import {createPortal} from 'react-dom';
import {TEAM_ROLES} from '../src/team';
import type {NativeAgentView, NativeChatActivity, NativeChatTurnActivity} from '../src/native-hermes-types';
import type {NativeWorkDetail} from './lib/types';
import {nativeExecutionStatus, nativeInputRequests} from './team-workspace';
import {ErrorNotice} from './components';
import {ChatTurnActivityDetails, ChatWorkTimer} from './chat-activity';
import {userMessageDisplayText} from './lib/conversation-messages';

type WorkStatusProps = {
  detail: NativeWorkDetail | null;
  sessionId: string | null;
  selectedSessionId: string | null;
  activity: NativeChatActivity | undefined;
  connected: boolean;
  loading: boolean;
  replyStatus: 'sending' | 'preparing' | null;
  messagePrefix: string | null;
  detailsOpen: boolean;
  detailsLoading: boolean;
  detailsContainer: HTMLElement | null;
  onDetailsOpenChange: (open: boolean) => void;
  onSelectAgent: (id: string | null) => void;
};

function latestRequestTurns(activity: NativeChatActivity | undefined): NativeChatTurnActivity[] {
  const latest = activity?.turns.at(-1);
  if (!latest) return [];
  return latest.userRowId === null ? [latest] : activity!.turns.filter(turn => turn.userRowId === latest.userRowId);
}

/** The current native response owns this view, independently of chart/task layout. */
export function NativeWorkStatus({detail, sessionId, selectedSessionId, activity, connected, loading, replyStatus, messagePrefix,
  detailsOpen, detailsLoading, detailsContainer, onDetailsOpenChange, onSelectAgent}: WorkStatusProps) {
  const detailsId = useId();
  const detailsElement = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = detailsElement.current;
    if (!detailsOpen || !detailsContainer || !element || !detailsContainer.contains(element)) return;
    detailsContainer.scrollTop += element.getBoundingClientRect().top - detailsContainer.getBoundingClientRect().top - detailsContainer.clientTop;
  }, [detailsOpen, detailsContainer]);
  const replyLabel = replyStatus === 'sending' ? 'Sending your message' : replyStatus === 'preparing' ? 'EightBots is preparing a reply' : null;
  if (!connected || !detail || detail.session.sessionId !== sessionId) {
    return <div className="chat-live-work-status" role="status" aria-live="polite">
      {replyLabel ?? (loading ? 'Checking work status' : 'Work status is unavailable')}
    </div>;
  }
  const selected = selectedSessionId ?? detail.session.sessionId;
  const requests = nativeInputRequests(detail);
  const inputOwners = new Set(requests.map(request => request.sessionId));
  for (const connection of detail.pendingConnections) inputOwners.add(connection.sessionId);
  const entries = [
    {sessionId: detail.session.sessionId, name: TEAM_ROLES.find(role => role.id === 'head')!.name, live: detail.state,
      todos: detail.todoState, work: detail.chatActivity},
    ...detail.agents.map((agent: NativeAgentView) => ({sessionId: agent.sessionId,
      name: agent.roleId === null ? messagePrefix && agent.title ? userMessageDisplayText(agent.title, messagePrefix, {folded: true}) : 'Assigned agent'
        : TEAM_ROLES.find(role => role.id === agent.roleId)!.name,
      live: agent, todos: agent.todoState, work: agent.sessionId === selected && activity !== undefined ? activity : agent.chatActivity}))
  ].map(entry => ({...entry, ...nativeExecutionStatus(entry.live, inputOwners.has(entry.sessionId))}));
  const lastTurn = activity?.turns.at(-1);
  const assigned = new Set(latestRequestTurns(activity).flatMap(turn =>
    turn.agents.flatMap(agent => agent.sessionId === null ? [] : [agent.sessionId])));
  const scope = new Set([selected]);
  if (selectedSessionId === null) for (const agent of detail.agents) scope.add(agent.sessionId);
  else {
    let size: number;
    do {
      size = scope.size;
      for (const agent of detail.agents) if (agent.parentSessionId !== null && scope.has(agent.parentSessionId)) scope.add(agent.sessionId);
    } while (scope.size !== size);
  }
  const rows = entries.filter(entry => entry.sessionId === selected || scope.has(entry.sessionId) && (
    entry.executionState === 'working' || entry.executionState === 'resuming' || entry.executionState === 'waiting_input'
    || entry.executionState === 'waiting_agents' || assigned.has(entry.sessionId)));
  const selectedEntry = rows.find(entry => entry.sessionId === selected);
  if (!selectedEntry) {
    return <div className="chat-live-work-status" role="status" aria-live="polite">Work status is unavailable</div>;
  }
  const working = rows.some(entry => entry.executionState === 'working');
  const waiting = rows.some(entry => entry.executionState === 'waiting_input');
  const starting = rows.some(entry => entry.executionState === 'resuming');
  const waitingForAgents = rows.some(entry => entry.executionState === 'waiting_agents');
  // Saved failures in older specialist assignments do not own the current turn's outcome.
  const failed = lastTurn ? lastTurn.status === 'failed' || lastTurn.status === 'interrupted'
    : selectedEntry.executionState === 'failed' || selectedEntry.executionState === 'interrupted';
  const completed = lastTurn ? lastTurn.status === 'completed' : selectedEntry.executionState === 'completed';
  const label = working ? waiting ? 'EightBots is working · Waiting for your answer' : 'EightBots is working'
    : waiting ? 'Waiting for your answer' : starting ? 'Starting work' : waitingForAgents ? 'Waiting for agent results'
    : replyLabel ?? (failed ? 'Work stopped' : completed ? 'Work completed' : 'No work is running');
  return <><div className="chat-live-work-status">
    <button type="button" className="chat-live-work-toggle" aria-expanded={detailsOpen} aria-controls={detailsId}
      onClick={() => onDetailsOpenChange(!detailsOpen)}>
      <span role="status" aria-live="polite" aria-atomic="true">{label}</span>
      <span className="chat-live-work-toggle-label">{detailsOpen ? 'Hide details' : 'Show details'}</span>
    </button>
  </div>
    {detailsOpen && detailsContainer && createPortal(<section className="chat-live-work-details" id={detailsId} ref={detailsElement} aria-label="Work details">
      <h3>Work details</h3>
      {detailsLoading && <p role="status">Loading work details</p>}
      {replyLabel && replyLabel !== label && <p>{replyLabel}</p>}
      {rows.map(entry => {
        const stopped = entry.executionState === 'failed' || entry.executionState === 'interrupted';
        const active = entry.executionState === 'working' || entry.executionState === 'resuming'
          || entry.executionState === 'waiting_input' || entry.executionState === 'waiting_agents';
        const turns = latestRequestTurns(entry.work);
        const runningTurn = active ? turns.filter(item => item.status === 'running').at(-1) : undefined;
        const turn = runningTurn ?? turns.at(-1);
        const recorded = !active && turn !== undefined;
        const status = !recorded ? entry.status : turn.status === 'completed' ? 'Completed' : turn.status === 'failed' ? 'Failed'
          : turn.status === 'interrupted' ? 'Interrupted' : turn.status === 'running' ? 'Last seen working' : 'Status not recorded';
        const todos = entry.todos?.todos.filter(todo => todo.status !== 'cancelled') ?? [];
        const task = active && messagePrefix && entry.live.assignedTask
          ? userMessageDisplayText(entry.live.assignedTask, messagePrefix) : null;
        const ownedRequests = requests.filter(request => request.sessionId === entry.sessionId);
        return <section className="chat-live-agent-work" key={entry.sessionId}>
          <h4>{recorded && 'Last recorded work: '}<button type="button" className="text-button" onClick={() => onSelectAgent(entry.sessionId === detail.session.sessionId ? null : entry.sessionId)}>{entry.name}</button>: {status}</h4>
          {task && <p><span className="chat-live-work-label">Assigned task</span>{task}</p>}
          {active && messagePrefix && todos.length > 0 && <ul className="chat-live-work-todos" aria-label="Task plan">{todos.map(todo =>
            <li key={todo.id}><span>{userMessageDisplayText(todo.content, messagePrefix)}</span><small>{todo.status === 'in_progress' ? 'In progress' : todo.status === 'completed' ? 'Done' : 'Next'}</small></li>)}</ul>}
          {active && entry.live.currentTool && <p>Current tool: {entry.live.currentTool.replaceAll('_', ' ')}</p>}
          {ownedRequests.map(request => <div className="chat-live-work-request" key={request.id}>
            <p>{request.title}</p>{request.questions.map(question => <p key={question.id}>{question.question}</p>)}
            {request.note && <p>{request.note}</p>}
          </div>)}
          {detail.pendingConnections.some(connection => connection.sessionId === entry.sessionId) && <p>A service connection needs your input.</p>}
          {turns.length > 0 && entry.work && <>
            {active && !runningTurn && <p className="chat-recorded-heading">Latest recorded steps</p>}
            {turns.map(recordedTurn => <section className="chat-live-work-turn" key={recordedTurn.id}>
              <p className="chat-live-work-elapsed"><ChatWorkTimer turn={recordedTurn} observedAt={entry.work!.observedAt}
                connected={connected && active} inline statusLabel={status}/></p>
              <ChatTurnActivityDetails turn={recordedTurn} connected={connected && active} expanded agents={detail.agents} onSelectAgent={onSelectAgent}
                {...(messagePrefix === null ? {} : {messagePrefix})}/>
            </section>)}
          </>}
          {active && !detailsLoading && entry.work?.available === false && <p>{entry.work.reason}</p>}
          {active && !detailsLoading && entry.work === undefined && <p>Recorded tool steps are unavailable.</p>}
          {stopped && entry.live.error && <ErrorNotice title="Session error" error={{name: 'AgentError', message: entry.live.error}}/>}
        </section>;
      })}
    </section>, detailsContainer)}
  </>;
}
